import { useEffect, useState } from 'react';
import kdfgLogo from './assets/kdfg-logo.png';
import luxBg from './assets/lux-bg.jpg';

interface PriceItem {
  metal: string;
  unit?: string;
  buy?: number;
  sell?: number;
  note?: string;
  deltaBuy?: { pct: number; abs: number };
  deltaSell?: { pct: number; abs: number };
}

interface PriceData {
  asOf: string;
  source: string;
  items: PriceItem[];
}

// API 기본 URL
// Netlify에서는 _redirects 파일을 통해 /api/* 요청이 backend.youware.com으로 프록시됩니다
const API_BASE_URL = '/api';

// Fallback 데이터 (API 실패시 사용)
const fallbackData: PriceData = {
  asOf: new Date().toISOString(),
  source: 'koreagoldx',
  items: [
    {
      metal: '24K 순금시세',
      unit: '3.75g',
      buy: 385000,
      sell: 345000,
      deltaBuy: { pct: 0.52, abs: 2000 },
      deltaSell: { pct: 0.58, abs: 2000 },
    },
    {
      metal: '18K 금시세',
      note: '제품 시세 적용',
      sell: 256000,
      deltaSell: { pct: 0.39, abs: 1000 },
    },
    {
      metal: '14K 금시세',
      note: '제품 시세 적용',
      sell: 198000,
      deltaSell: { pct: 0.51, abs: 1000 },
    },
    {
      metal: '백금시세',
      unit: '3.75g',
      buy: 172000,
      sell: 145000,
      deltaBuy: { pct: -0.35, abs: -600 },
      deltaSell: { pct: -0.34, abs: -500 },
    },
    {
      metal: '은시세',
      unit: 'g',
      buy: 4520,
      sell: 3600,
      deltaBuy: { pct: 1.12, abs: 50 },
      deltaSell: { pct: 1.41, abs: 50 },
    },
  ],
};

// 백엔드에서 데이터 가져오기 (강제 새로고침)
async function fetchPriceData(forceRefresh = false): Promise<PriceData> {
  try {
    // forceRefresh가 true이면 POST /api/kdfg/fetch 호출 (새 데이터 강제 가져오기)
    if (forceRefresh) {
      const refreshResponse = await fetch(`${API_BASE_URL}/api/kdfg/fetch`, {
        method: 'POST',
      });
      const refreshResult = await refreshResponse.json();
      
      if (refreshResult.success && refreshResult.data) {
        return refreshResult.data;
      }
    }
    
    // 일반적인 경우 GET /api/kdfg/daily 호출
    const response = await fetch(`${API_BASE_URL}/api/kdfg/daily`);
    const result = await response.json();
    
    if (result.success && result.data) {
      return result.data;
    }
    
    console.error('Failed to fetch price data:', result.error);
    return fallbackData;
  } catch (error) {
    console.error('API call failed, using fallback data:', error);
    return fallbackData;
  }
}

function App() {
  const [data, setData] = useState<PriceData>(fallbackData);
  const [currentDate, setCurrentDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 초기 데이터 로드 (강제 새로고침으로 최신 데이터 가져오기)
    const loadData = async (forceRefresh = false) => {
      setLoading(true);
      const priceData = await fetchPriceData(forceRefresh);
      setData(priceData);
      setLoading(false);
    };
    
    // 첫 로드 시 강제 새로고침
    loadData(true);
    
    // 10초마다 자동으로 데이터 새로고침 (일반 조회)
    const interval = setInterval(() => loadData(false), 10 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // 날짜 포맷팅
    const date = new Date(data.asOf);
    const formatted = date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    setCurrentDate(formatted.replace(/\. /g, '-').replace('.', ''));
  }, [data]);

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const getDeltaClass = (pct: number) => {
    if (pct > 0) return 'text-[#22C55E]';
    if (pct < 0) return 'text-[#FF6B6B]';
    return 'text-[#9AA3B2]';
  };

  const getDeltaIcon = (pct: number) => {
    if (pct > 0) return '▲';
    if (pct < 0) return '▼';
    return '—';
  };

  const renderDelta = (delta?: { pct: number; abs: number }) => {
    if (!delta) return null;
    const { pct, abs } = delta;
    const deltaClass = getDeltaClass(pct);
    const icon = getDeltaIcon(pct);

    return (
      <div className={`text-[18px] mt-1 ${deltaClass}`}>
        {icon} {Math.abs(pct).toFixed(2)}% {abs !== 0 && `${abs > 0 ? '+' : ''}${formatPrice(abs)}`}
      </div>
    );
  };

  // 로딩 상태에서도 데이터 표시 (fallback 사용)
  if (loading) {
    // 로딩 중에도 화면 표시 (투명도만 조정)
  }

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-sans relative overflow-hidden">
      {/* 배경 레이어 - 동적 그라데이션 애니메이션 (영상처럼) */}
      <div className="fixed inset-0 z-[-2]">
        {/* 다크 베이스 */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0B0F] via-[#1a1410] to-[#0A0B0F]"></div>
        
        {/* 움직이는 골드 그라데이션 레이어 1 */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#F0B54A]/10 to-transparent animate-goldflow1"></div>
        
        {/* 움직이는 골드 그라데이션 레이어 2 */}
        <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-[#D4A04C]/8 to-transparent animate-goldflow2"></div>
        
        {/* 빛 스윕 효과 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-[200%] h-[200%] -top-[50%] -left-[50%] bg-[conic-gradient(from_0deg,transparent_0deg,rgba(240,181,74,0.15)_60deg,transparent_120deg)] animate-lightsweep"></div>
        </div>
        
        {/* 파티클 효과 (작은 빛나는 점들) */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[20%] left-[15%] w-1 h-1 bg-[#F0B54A] rounded-full animate-sparkle1"></div>
          <div className="absolute top-[45%] left-[75%] w-1 h-1 bg-[#F0B54A] rounded-full animate-sparkle2"></div>
          <div className="absolute top-[65%] left-[35%] w-1 h-1 bg-[#F0B54A] rounded-full animate-sparkle3"></div>
          <div className="absolute top-[80%] left-[85%] w-1 h-1 bg-[#F0B54A] rounded-full animate-sparkle1"></div>
          <div className="absolute top-[30%] left-[60%] w-1 h-1 bg-[#D4A04C] rounded-full animate-sparkle2"></div>
          <div className="absolute top-[55%] left-[20%] w-1 h-1 bg-[#D4A04C] rounded-full animate-sparkle3"></div>
        </div>
        
        {/* 다크 오버레이 */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0F]/60 via-transparent to-[#0A0B0F]/60"></div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="relative z-10 flex flex-col items-center justify-between min-h-screen px-8 py-12">
        {/* 헤더 */}
        <div className="text-center animate-fadeup">
          <h1 className="text-[72px] font-extrabold leading-none tracking-tight mb-2">
            오늘의 <span className="text-[#F0B54A]">금</span> 시세
          </h1>
          <p className="text-[28px] text-[#A9B0BD] font-medium">{currentDate} 기준</p>
        </div>

        {/* 시세 보드 */}
        <div className="w-full max-w-[900px] animate-fadeup animation-delay-200">
          <div className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-[24px] p-7 backdrop-blur-sm">
            {/* 헤더 행 */}
            <div className="grid grid-cols-2 gap-6 pb-5 border-b border-[rgba(255,255,255,0.08)]">
              <div className="text-center">
                <div className="text-[#F0B54A] text-[24px] font-bold">구입시</div>
              </div>
              <div className="text-center">
                <div className="text-[#F0B54A] text-[24px] font-bold">판매시</div>
              </div>
            </div>

            {/* 시세 행들 */}
            {data.items.map((item, index) => (
              <div
                key={index}
                className={`grid grid-cols-2 gap-6 py-6 ${
                  index !== data.items.length - 1 ? 'border-b border-[rgba(255,255,255,0.08)]' : ''
                }`}
              >
                {/* 왼쪽: 구입시 */}
                <div className="flex flex-col items-center justify-center">
                  <div className="text-[#F0B54A] text-[22px] font-bold mb-2">
                    {item.metal}
                    {item.unit && <span className="text-[18px] text-[#A9B0BD] ml-2">({item.unit})</span>}
                  </div>
                  {item.buy ? (
                    <>
                      <div className="text-[48px] font-extrabold">{formatPrice(item.buy)}원</div>
                      {renderDelta(item.deltaBuy)}
                    </>
                  ) : (
                    <>
                      <div className="text-[#A9B0BD] text-[20px]">—</div>
                      {item.note ? (
                        <div className="inline-block px-4 py-2 bg-[rgba(240,181,74,0.12)] border border-[rgba(240,181,74,0.28)] rounded-[10px] text-[18px] text-[#F0B54A] mt-3">
                          {item.note}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {/* 오른쪽: 판매시 */}
                <div className="flex flex-col items-center justify-center">
                  {item.sell ? (
                    <>
                      <div className="text-[48px] font-extrabold">{formatPrice(item.sell)}원</div>
                      {renderDelta(item.deltaSell)}
                    </>
                  ) : (
                    <div className="text-[#A9B0BD] text-[20px]">—</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 푸터 */}
        <div className="text-center animate-fadeup animation-delay-400">
          <div className="text-[64px] font-black tracking-wider mb-3">1660-1824</div>
          <img src={kdfgLogo} alt="한국금융금거래소" className="h-[46px] mx-auto opacity-95" />
          <div className="mt-8 text-[14px] text-[#A9B0BD] leading-relaxed max-w-[800px] mx-auto">
            <p>본 화면은 안내용이며 실제 거래는 수수료·가공비·부가세 등 조건에 따라 달라질 수 있습니다.</p>
            <p className="mt-1">출처: 한국금거래소. 데이터 갱신 과정에서 일시적 지연이 발생할 수 있습니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
